/**
 * Simple in-memory cache for client-side API responses.
 * Survives React route unmount/remount (e.g. Home → Product → Home).
 */

const cache = new Map();

/**
 * @param {string} key
 * @param {*} data
 */
export function setCacheEntry(key, data) {
    cache.set(key, { data, fetchedAt: Date.now() });
}

/**
 * @param {string} key
 * @param {number} ttlMs
 * @returns {*|null}
 */
export function getCacheEntry(key, ttlMs) {
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.fetchedAt > ttlMs) {
        cache.delete(key);
        return null;
    }
    return entry.data;
}

/**
 * @param {string} key
 */
export function invalidateCacheEntry(key) {
    cache.delete(key);
}

export function clearFetchCache() {
    cache.clear();
}

/**
 * Fetch with deduplication and TTL. Returns cached data when fresh.
 * @param {string} key
 * @param {() => Promise<*>} fetcher
 * @param {{ ttlMs?: number }} options
 */
export async function cachedFetch(key, fetcher, { ttlMs = 5 * 60 * 1000 } = {}) {
    const fresh = getCacheEntry(key, ttlMs);
    if (fresh != null) {
        return fresh;
    }

    const existing = cache.get(key);
    if (existing?.inFlight) {
        return existing.inFlight;
    }

    const inFlight = fetcher()
        .then((data) => {
            setCacheEntry(key, data);
            const entry = cache.get(key);
            if (entry) entry.inFlight = null;
            return data;
        })
        .catch((err) => {
            const entry = cache.get(key);
            if (entry?.inFlight) {
                cache.delete(key);
            }
            throw err;
        });

    cache.set(key, { ...(existing || {}), inFlight });
    return inFlight;
}
